/* Sibling Switcher — instant colorway switching between sibling PRODUCTS.
 *
 * Each colorway is its own Shopify product with its own URL. The picker
 * (snippets/sibling-swatches.liquid) renders real <a href> links; this module
 * progressively enhances them: on click it fetches the sibling product's
 * server-rendered PDP section via the Section Rendering API
 * (/products/<handle>?sections=<ids>), swaps the related sections in place,
 * updates the URL via
 * history.pushState, refreshes head metadata from the section's context
 * island, and emits the storefront's exactly-once product/switch data-layer
 * events. GA4 Enhanced Measurement observes the history change for page_view.
 *
 * Safety properties:
 *  - Any failure falls back to normal full navigation (worst case = today).
 *  - Modifier/middle clicks are never intercepted.
 *  - AbortController cancels stale fetches; a monotonically increasing token
 *    guarantees an earlier response can never overwrite a later selection.
 *  - Back/forward is handled via popstate re-using the same swap path.
 *  - Node-bound inline section scripts re-init via the
 *    'soolla:product-swapped' event dispatched after every swap.
 *
 * Switch latency (2026-09-25):
 *  - Optimistic buy box: on tap the tile ring, H1 (both tiers), price and the
 *    hero (decoded, then cross-faded over the outgoing hero) update from the
 *    tile's data-sibsw-opt JSON while the section fetch is in flight. URL,
 *    <title>, canonical/og, history and analytics still change exactly once, at
 *    commit, so superseded rage taps leave no history entries or events.
 *  - The buy box gets aria-busy while a switch is pending (the 2px progress
 *    bar that used to appear under the swatch grid was removed 2026-09-26 at
 *    Andy's request: on bundle/pouch pages the grid sits right above the buy
 *    box and the bar read as a loader on the add-to-cart button).
 *  - Add-to-cart / dynamic checkout taps made while the optimistic buy box is
 *    ahead of the form are held and the add is replayed on the committed
 *    colourway, so the cart always gets the colourway the shopper sees.
 *  - Same-tile repeat taps during a switch are ignored (no double fetch);
 *    different tiles are latest-wins.
 *  - Fetch starts on touchstart / pointerdown; the first six thumbnails of the
 *    incoming gallery load before the commit; neighbours of the committed tile
 *    are prefetched at idle (budgeted, skipped on Save-Data / 2G).
 */
(function () {
  'use strict';
  if (window.__soollaSiblingSwitcher) return;
  window.__soollaSiblingSwitcher = true;

  var CACHE_TTL = 5 * 60 * 1000;
  var CACHE_MAX = 10;
  var REQUEST_TIMEOUT = 8000;
  var SWITCH_TIMEOUT = 12000;
  var cache = new Map(); // request key -> { sections: object, ts: number }
  var inFlight = new Map(); // request key -> shared request + active consumers
  var switchController = null;
  var prefetchController = null;
  var prefetchHref = null;
  var switchToken = 0;
  var committing = false; // true only inside the synchronous DOM swap
  var hoverTimer = null;
  var liveRegion = null;
  var pending = null; // { path, token } of the switch in flight
  var optimistic = null; // optimistic buy-box state layered on the committed DOM
  var heroCache = new Map(); // srcset|src -> { img, promise }
  var idleBudget = 8; // idle neighbour prefetches per page view (~18 KB brotli each)
  var idleInFlight = 0;
  var THUMBS_BEFORE_COMMIT = 6;

  function getRoot() {
    return Array.prototype.find.call(
      document.querySelectorAll('product-information[data-section][data-product-handle]'),
      function (root) {
        return !root.closest('[data-sibsw-stage]');
      }
    );
  }

  function getSectionEl(root) {
    var el = root;
    while (el && el !== document.body) {
      if (el.id && el.id.indexOf('shopify-section-') === 0) return el;
      el = el.parentElement;
    }
    return null;
  }

  function getSectionBundle(root) {
    var primary = root && getSectionEl(root);
    var primaryId = root && root.dataset.section;
    if (!primary || !primaryId) return null;

    var marker = primaryId.indexOf('__');
    var prefix = marker === -1 ? '' : primaryId.slice(0, marker + 2);
    var contentId = prefix ? prefix + 'product-content' : '';
    var sections = [{ id: primaryId, element: primary, required: true }];

    [{ id: contentId, required: true }].forEach(function (item) {
      if (!item.id) return;
      var element = document.getElementById('shopify-section-' + item.id);
      if (element) sections.push({ id: item.id, element: element, required: item.required });
    });

    return {
      primaryId: primaryId,
      primary: primary,
      sections: sections,
      ids: sections.map(function (item) {
        return item.id;
      }),
    };
  }

  // Carry the outgoing "Make it a set" choice onto the hidden staged section
  // BEFORE it is revealed. The fresh HTML always arrives with "Just the bag"
  // selected (server default) and custom-product-information's init re-applies
  // the preserved choice only on 'soolla:product-swapped', i.e. after the
  // reveal: the tiles' 0.15s border / radio-dot transitions (bundle-upsell.css)
  // then crossfade bag -> bundle in the first frames of the new section, which
  // reads as the selection "jumping back to the bag" (Andy, desktop Chrome,
  // 2026-09-26). Pre-applying the same values makes that init a visual no-op;
  // .sibsw-preapply (sibling-swatches.css) also disables those transitions for
  // the reveal frame, removed two frames after commit.
  function preApplyBundleSelection(section, state) {
    if (!section || !state || !state.bundleSelection) return;
    // [data-apw-solo] = the BYO-only set block on bag-only colourways (apw-widget.liquid
    // 'solo' part): same tiles, radios and dataset names, no bundle variant / price.
    var fs = section.querySelector('[data-bndl], [data-apw-solo]');
    if (!fs) return;
    var isB = state.bundleSelection === 'bundle';
    if (isB && !fs.querySelector('input[type="radio"][value="bundle"]')) return; // bundle-less colourway
    var want = isB ? 'bundle' : 'bag';
    fs.querySelectorAll('input[type="radio"]').forEach(function (input) {
      input.checked = input.value === want;
    });
    fs.dataset.selected = want;
    fs.classList.add('sibsw-preapply');
    fs.querySelectorAll('.bndl__opt').forEach(function (o) {
      var i = o.querySelector('input');
      o.classList.toggle('is-selected', !!(i && i.checked));
    });
    var card = fs.querySelector('[data-apw-card]');
    if (card) card.classList.toggle('is-selected', isB);
    var variantId = fs.getAttribute(isB ? 'data-bundle-variant' : 'data-bag-variant');
    if (variantId) {
      section.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach(function (i) {
        i.value = variantId;
      });
    }
    var money = isB ? fs.getAttribute('data-bundle-price') || fs.getAttribute('data-set-price') : fs.getAttribute('data-bag-price');
    if (money) {
      var priceToken = /(?:[A-Z]{1,3})?[€£$]\s?[\d.,]+(?:\s+[A-Z]{3})?/;
      section.querySelectorAll('.product-form__buy-buttons button, .product-form__submit, [name="add"]').forEach(function (btn) {
        var walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT, null);
        var node;
        while ((node = walker.nextNode())) {
          if (priceToken.test(node.nodeValue)) {
            node.nodeValue = node.nodeValue.replace(priceToken, money);
            break;
          }
        }
      });
    }
  }

  function releasePreApply(section) {
    if (!section) return;
    var fs = section.querySelector('[data-bndl].sibsw-preapply, [data-apw-solo].sibsw-preapply');
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (fs) fs.classList.remove('sibsw-preapply');
        // .sibsw-fresh (set in restoreStagedPrimary) suppressed the theme's
        // visibility transitions on the add-to-cart button for the commit
        // frame: the label's 100ms transition-delay otherwise left the button
        // red but textless for the first frames after a switch.
        section.classList.remove('sibsw-fresh');
      });
    });
  }

  function captureTransientState(root) {
    var state = {};
    var bundle = root && root.querySelector('[data-bndl], [data-apw-solo]');
    if (bundle) {
      var checkedBundle = bundle.querySelector('input[type="radio"][value="bundle"]:checked');
      state.bundleSelection = checkedBundle || bundle.dataset.selected === 'bundle' ? 'bundle' : 'bag';
      // BYO / Any-Pouch Widget (apw-widget.js): the shopper's pouch pick survives a
      // colourway switch. Read at commit from the outgoing live fieldset, re-applied
      // by the widget's init on 'soolla:product-swapped' (falls back to the match).
      if (bundle.dataset.apwMode) {
        state.apw = {
          mode: bundle.dataset.apwMode,
          pouch: bundle.dataset.apwPouch || '',
          pouchVariant: bundle.dataset.apwPouchVariant || '',
        };
      }
    }

    var pdpv2 = root && root.querySelector('[data-pdpv2-bundle]');
    if (pdpv2) {
      var pdpv2Bundle = pdpv2.querySelector('input[type="radio"][value="bundle"]:checked');
      var pdpv2Addon = pdpv2.querySelector('input[type="checkbox"][data-pdpv2-addon]:checked');
      state.pdpv2BundleSelection = pdpv2Bundle || pdpv2Addon || pdpv2.dataset.selected === 'bundle' ? 'bundle' : 'single';
    }
    return state;
  }

  function captureSwatchAnchor(tile) {
    if (!tile || !window.matchMedia('(max-width: 1000px)').matches) return null;
    var handle = tile.getAttribute('data-sibling-handle');
    var rect = tile.getBoundingClientRect();
    if (!handle || !rect) return null;
    return { handle: handle, top: rect.top };
  }

  function restoreSwatchAnchor(section, anchor, scrollPosition) {
    if (!section || !anchor || !anchor.handle) return scrollPosition;
    var incomingTile = Array.prototype.find.call(
      section.querySelectorAll('[data-sibling-swatches] a[data-sibling-handle]'),
      function (tile) {
        return tile.getAttribute('data-sibling-handle') === anchor.handle;
      }
    );
    if (!incomingTile) return scrollPosition;
    var incomingTop = incomingTile.getBoundingClientRect().top;
    if (!Number.isFinite(incomingTop) || !Number.isFinite(anchor.top)) return scrollPosition;
    scrollPosition.y = Math.max(0, scrollPosition.y + incomingTop - anchor.top);
    window.scrollTo(scrollPosition.x, scrollPosition.y);
    return scrollPosition;
  }

  function captureLayoutContract(section) {
    var info = section && section.querySelector('.product__info-wrapper');
    var gallery = section && section.querySelector('media-gallery');
    var slider = gallery && gallery.querySelector('.product-single__gallery');
    var content = section && section.querySelector('.product-page__content');
    var infoRect = info && info.getBoundingClientRect();
    var galleryRect = gallery && gallery.getBoundingClientRect();
    var contentRect = content && content.getBoundingClientRect();
    return {
      // product-content.js pins the published PDP info column to 538px after
      // first load. Its custom element is already initialized during an
      // in-place switch, so replacing the info node would otherwise drop the
      // inline pin and visibly widen/shift the column for one or more frames.
      infoMaxWidth: info ? info.style.maxWidth : '',
      infoX: infoRect ? infoRect.x : 0,
      infoTop: infoRect ? infoRect.top : 0,
      infoWidth: infoRect ? infoRect.width : 0,
      // The temporary fixed-position pin is only valid while the buy tools
      // are a desktop column beside the gallery. On mobile the info panel is
      // part of the vertical document flow; fixing it lets the description
      // rise underneath the panel until the lock releases.
      pinInfoPosition: Boolean(
        infoRect &&
          galleryRect &&
          window.matchMedia('(min-width: 1001px)').matches &&
          Math.abs(infoRect.top - galleryRect.top) <= 2
      ),
      galleryWidth: galleryRect ? galleryRect.width : 0,
      galleryHeight: slider ? slider.getBoundingClientRect().height : 0,
      contentWidth: contentRect ? contentRect.width : 0,
    };
  }

  function restoreLayoutContract(section, state) {
    if (!section || !state) return;
    var info = section.querySelector('.product__info-wrapper');
    var gallery = section.querySelector('media-gallery');
    var slider = gallery && gallery.querySelector('.product-single__gallery');
    var content = section.querySelector('.product-page__content');
    var locks = [];

    function lockStyle(node, property, value) {
      if (!node || value === null || typeof value === 'undefined') return;
      locks.push({ node: node, property: property, value: node.style[property] });
      node.style[property] = value;
    }

    function lock(node, property, value) {
      if (!value) return;
      lockStyle(node, property, value + 'px');
    }

    if (info && state.infoMaxWidth) info.style.maxWidth = state.infoMaxWidth;
    lock(content, 'width', state.contentWidth);
    lock(content, 'minWidth', state.contentWidth);
    lock(info, 'width', state.infoWidth);
    lock(info, 'minWidth', state.infoWidth);
    lock(info, 'flexBasis', state.infoWidth);
    lock(gallery, 'width', state.galleryWidth);
    lock(gallery, 'minWidth', state.galleryWidth);
    lock(gallery, 'flexBasis', state.galleryWidth);
    lock(slider, 'height', state.galleryHeight);
    lock(slider, 'minHeight', state.galleryHeight);
    try {
      if (
        slider &&
        slider.classList.contains('slick-initialized') &&
        window.theme &&
        typeof window.theme.jQuery === 'function'
      ) {
        window.theme.jQuery(slider).slick('setPosition');
      }
    } catch (e) {
      /* dimension locks remain the safe fallback */
    }

    if (info && state.pinInfoPosition) {
      lockStyle(info, 'position', 'fixed');
      lockStyle(info, 'top', state.infoTop + 'px');
      lockStyle(info, 'left', state.infoX + 'px');
      lockStyle(info, 'right', 'auto');
      lockStyle(info, 'bottom', 'auto');
    }

    section.querySelectorAll('.product-single__media-wrapper').forEach(function (wrapper) {
      locks.push({ node: wrapper, property: 'backgroundColor', value: wrapper.style.backgroundColor });
      wrapper.style.backgroundColor = 'rgb(239, 238, 236)';
    });

    // The outgoing geometry stays pinned through the browser's delayed
    // enquire/Slick responsive callbacks. The incoming nodes are already at
    // these exact dimensions, so releasing the locks does not animate.
    window.setTimeout(function () {
      window.requestAnimationFrame(function () {
        locks.forEach(function (item) {
          item.node.style[item.property] = item.value;
        });
      });
    }, 450);
  }

  function captureFamilyReviewState(section) {
    var root = section && section.querySelector('product-information[data-product-family]');
    var ratingLink = section && section.querySelector(
      '.product__info-container .shopify-app-block a[href^="#looxReviews"]'
    );
    var ratingBlock = ratingLink && ratingLink.closest('.shopify-app-block');
    var family = root && root.dataset.productFamily;
    if (!family || !ratingBlock) return null;
    return { family: family, ratingBlock: ratingBlock };
  }

  function restoreFamilyReviewState(section, state) {
    if (!section || !state || !state.ratingBlock) return;
    var root = section.querySelector('product-information[data-product-family]');
    if (!root || root.dataset.productFamily !== state.family) return;

    var incomingLink = section.querySelector(
      '.product__info-container .shopify-app-block a[href^="#looxReviews"]'
    );
    var incomingBlock = incomingLink && incomingLink.closest('.shopify-app-block');
    // Loox review totals are grouped by product family. Reinsert the already
    // upgraded rating node instead of rebuilding the widget for every colour.
    if (incomingBlock) incomingBlock.replaceWith(state.ratingBlock);
  }

  function getContext(scope) {
    var island = (scope || document).querySelector('script[data-sibling-context]');
    if (!island) return null;
    try {
      return JSON.parse(island.textContent);
    } catch (e) {
      return null;
    }
  }

  function sectionsUrlFor(href, sectionIds) {
    var url = new URL(href, window.location.origin);
    url.search = '';
    url.searchParams.set('sections', sectionIds.join(','));
    // Always hand fetch() a STRING: at least one app on the page wraps
    // window.fetch assuming a string URL (calls url.includes(...)).
    return url.toString();
  }

  function requestKeyFor(href, bundle) {
    var url = new URL(href, window.location.origin);
    return url.pathname + '::' + bundle.ids.join(',');
  }

  function cacheGet(key) {
    var hit = cache.get(key);
    if (!hit) return null;
    if (Date.now() - hit.ts > CACHE_TTL) {
      cache.delete(key);
      return null;
    }
    // Least-recently-used order: a hit moves to the newest end.
    cache.delete(key);
    cache.set(key, hit);
    return hit.sections;
  }

  function cacheSet(key, sections) {
    cache.set(key, { sections: sections, ts: Date.now() });
    while (cache.size > CACHE_MAX) {
      cache.delete(cache.keys().next().value);
    }
  }

  function abortError(message) {
    var error = new Error(message || 'sibling switch aborted');
    error.name = 'AbortError';
    return error;
  }

  function consumeRequest(entry, signal) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      entry.consumers += 1;

      function release() {
        if (settled) return false;
        settled = true;
        if (signal) signal.removeEventListener('abort', onAbort);
        entry.consumers -= 1;
        if (!entry.done && entry.consumers === 0) entry.controller.abort();
        return true;
      }

      function onAbort() {
        if (!release()) return;
        reject(abortError('sibling switch consumer aborted'));
      }

      if (signal) signal.addEventListener('abort', onAbort, { once: true });
      if (signal && signal.aborted) {
        onAbort();
        return;
      }

      entry.promise.then(
        function (sections) {
          if (!release()) return;
          resolve(sections);
        },
        function (error) {
          if (!release()) return;
          reject(error);
        }
      );
    });
  }

  function announce(text) {
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      liveRegion.style.cssText =
        'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
      document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = text;
  }

  function updateHead(ctx) {
    if (!ctx) return;
    var absUrl = window.location.origin + ctx.url;
    var documentTitle = ctx.seoTitle || ctx.title;
    if (documentTitle && ctx.shopName && documentTitle.indexOf(ctx.shopName) === -1) {
      documentTitle += ' – ' + ctx.shopName;
    }
    if (documentTitle) document.title = documentTitle;

    function setAttr(selector, attr, value) {
      var el = document.querySelector(selector);
      if (el && value) el.setAttribute(attr, value);
    }
    setAttr('link[rel="canonical"]', 'href', absUrl);
    setAttr('meta[name="description"]', 'content', ctx.seoDescription || '');
    setAttr('meta[property="og:title"]', 'content', ctx.seoTitle || ctx.title);
    setAttr('meta[property="og:url"]', 'content', absUrl);
    setAttr('meta[property="og:description"]', 'content', ctx.seoDescription || '');
    var img = ctx.image;
    if (img && img.indexOf('//') === 0) img = 'https:' + img;
    if (img) {
      setAttr('meta[property="og:image"]', 'content', img);
      setAttr('meta[name="twitter:image"]', 'content', img);
    }
    setAttr('meta[name="twitter:title"]', 'content', ctx.seoTitle || ctx.title);
  }

  function pushDataLayerEvent(name, params) {
    // This storefront's established GA4 bridge listens for sha_* data-layer
    // events. The optional global `gtag` convenience function is not present
    // on the real storefront, so calling it silently drops sibling events.
    window.dataLayer = window.dataLayer || [];
    var payload = Object.assign({ event: name }, params || {});
    window.dataLayer.push(payload);
  }

  function track(ctx, fromCtx) {
    try {
      if (!ctx) return;
      // Emit product/switch events once through the established GTM bridge.
      // Virtual page_view ownership still needs real DebugView validation;
      // do not assume Enhanced Measurement observes this history change.
      var value = (ctx.price || 0) / 100;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      pushDataLayerEvent('sha_view_item', {
        ecommerce: {
          currency: ctx.currency,
          value: value,
          items: [
            {
              index: 0,
              item_id: String(ctx.id),
              product_id: String(ctx.id),
              variant_id: String(ctx.variantId),
              item_name: ctx.title,
              quantity: 1,
              price: value,
              sku: ctx.sku || '',
            },
          ],
        },
      });
      pushDataLayerEvent('sha_sibling_switch', {
        from_handle: fromCtx ? fromCtx.handle : '(unknown)',
        to_handle: ctx.handle,
      });
    } catch (e) {
      /* tracking must never break the switch */
    }
  }

  function fetchSections(href, bundle, signal) {
    var key = requestKeyFor(href, bundle);
    var cached = cacheGet(key);
    if (cached) return Promise.resolve(cached);
    var entry = inFlight.get(key);
    if (!entry) {
      var controller = new AbortController();
      entry = {
        controller: controller,
        consumers: 0,
        done: false,
        timedOut: false,
        promise: null,
      };
      var timeout = window.setTimeout(function () {
        entry.timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT);

      entry.promise = fetch(sectionsUrlFor(href, bundle.ids), {
        // Do not send `Accept: application/json`: Shopify interprets that as
        // the Product Ajax API and returns `{ product: ... }` instead of the
        // requested Section Rendering payload.
        signal: controller.signal,
      })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (sections) {
          var primaryHtml = sections && sections[bundle.primaryId];
          if (typeof primaryHtml !== 'string' || primaryHtml.indexOf('data-sibling-context') === -1) {
            throw new Error('section missing context island');
          }
          bundle.sections.forEach(function (item) {
            if (item.required && typeof sections[item.id] !== 'string') {
              throw new Error('required section missing: ' + item.id);
            }
          });
          cacheSet(key, sections);
          return sections;
        })
        .catch(function (error) {
          if (!entry.timedOut) throw error;
          var timeoutError = new Error('section request exceeded ' + REQUEST_TIMEOUT + 'ms');
          timeoutError.name = 'TimeoutError';
          throw timeoutError;
        })
        .finally(function () {
          window.clearTimeout(timeout);
          entry.done = true;
          if (inFlight.get(key) === entry) inFlight.delete(key);
        });
      inFlight.set(key, entry);
    }

    return consumeRequest(entry, signal);
  }

  function pinStaticAssets() {
    var stylesheet = document.querySelector('link[href*="sibling-swatches.css"]');
    if (stylesheet && stylesheet.parentElement !== document.head) document.head.appendChild(stylesheet);
  }

  function prepareSectionSources(sections, bundle) {
    var sources = {};
    bundle.sections.forEach(function (item) {
      var html = sections[item.id];
      if (typeof html !== 'string') return;
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var source = doc.getElementById('shopify-section-' + item.id) || doc.body.firstElementChild;
      if (!source) {
        if (item.required) throw new Error('empty section response: ' + item.id);
        return;
      }
      sources[item.id] = source;
    });

    var primarySource = sources[bundle.primaryId];
    if (!primarySource) throw new Error('empty primary section response');
    // A swapped-in section must not replay the info container's entrance fade
    // (sibling-swatches.css) — it painted the buy box washed-out after commits.
    primarySource.setAttribute('data-sibsw-swapped', '');
    // The visible thumbnail strip loads with the swap instead of lazily after
    // it (Safari left the strip as empty boxes for ~0.5s once committed).
    primaryThumbnails(primarySource)
      .concat(bundleWidgetImages(primarySource))
      .forEach(function (img) {
        img.removeAttribute('loading');
      });
    // WebKit does not decode images inside the hidden stage; with
    // decoding="async" it then painted the whole swatch grid (and other small
    // images) as empty tiles for a frame when the stage became visible. The
    // bytes are already cached and decoded once, so a sync decode at first
    // paint costs nothing measurable.
    Array.prototype.slice
      .call(primarySource.querySelectorAll('[data-sibling-swatches] img'))
      .concat(primaryThumbnails(primarySource), bundleWidgetImages(primarySource))
      .forEach(function (img) {
        img.setAttribute('decoding', 'sync');
      });
    var incomingHero =
      primarySource.querySelector('.product-single__gallery-item img[fetchpriority="high"]') ||
      primarySource.querySelector('.product-single__gallery-item img');
    if (incomingHero) incomingHero.setAttribute('decoding', 'sync');
    // Keep the already-loaded switcher assets outside the swapped subtree.
    // Re-inserting the stylesheet caused a brief unstyled grid, blank tiles,
    // and the measured mobile scroll movement while the cached CSS re-applied.
    primarySource
      .querySelectorAll('link[href*="sibling-swatches.css"], script[src*="sibling-switcher.js"]')
      .forEach(function (asset) {
        asset.remove();
      });
    return sources;
  }

  function primaryThumbnails(scope) {
    return Array.prototype.slice
      .call(
        scope.querySelectorAll(
          '.product-single__thumbnails-item:not(.product-single__thumbnails--hide) img'
        )
      )
      .slice(0, THUMBS_BEFORE_COMMIT);
  }

  // "Make it a set" (and PDP-v2 bundle) thumbnails: small, above the fold next to
  // the swatches, lazy by default, so they committed as empty boxes.
  function bundleWidgetImages(scope) {
    return Array.prototype.slice.call(scope.querySelectorAll('[data-bndl] img, [data-pdpv2-bundle] img'));
  }

  function preloadSectionImages(primarySource) {
    // Swatch crops are the same URLs on every sibling: only wait for ones the
    // current page has not already loaded.
    var loaded = {};
    Array.prototype.forEach.call(document.querySelectorAll('[data-sibling-swatches] img'), function (img) {
      if (img.complete && img.naturalWidth) loaded[img.getAttribute('src')] = true;
    });
    var nodes = Array.prototype.slice
      .call(primarySource.querySelectorAll('[data-sibling-swatches] img'))
      .filter(function (img) {
        return !loaded[img.getAttribute('src')];
      });
    var hero =
      primarySource.querySelector('.product-single__gallery-item img[fetchpriority="high"]') ||
      primarySource.querySelector('.product-single__gallery-item img');
    if (hero) nodes.push(hero);
    nodes = nodes.concat(bundleWidgetImages(primarySource));
    return Promise.all([
      preloadImageNodes(nodes, 2500),
      // Thumbnails are small; never hold the commit on them for long.
      preloadImageNodes(primaryThumbnails(primarySource), 700),
    ]);
  }

  function preloadImageNodes(nodes, cap) {
    if (!nodes.length) return Promise.resolve();

    var seen = {};
    var loads = nodes
      .filter(function (node) {
        var src = node.getAttribute('src');
        if (!src || seen[src]) return false;
        seen[src] = true;
        return true;
      })
      .map(function (node) {
        return new Promise(function (resolve) {
          var image = new Image();
          var done = false;
          function finish() {
            if (done) return;
            done = true;
            if (typeof image.decode === 'function' && image.naturalWidth) {
              image.decode().catch(function () {}).then(resolve);
            } else {
              resolve();
            }
          }
          image.onload = finish;
          image.onerror = finish;
          var srcset = node.getAttribute('srcset');
          var sizes = node.getAttribute('sizes');
          if (srcset) image.srcset = srcset;
          if (sizes) image.sizes = sizes;
          image.src = node.getAttribute('src');
          if (image.complete) finish();
        });
      });

    return Promise.race([
      Promise.all(loads),
      new Promise(function (resolve) {
        window.setTimeout(resolve, cap);
      }),
    ]);
  }

  function preserveSharedStyles(sources, bundle, signal) {
    var loads = [];
    bundle.sections.forEach(function (item) {
      var source = sources[item.id];
      if (!source) return;
      source.querySelectorAll('link[rel="stylesheet"][href]').forEach(function (incoming) {
        var href = new URL(incoming.getAttribute('href'), window.location.href).href;
        var links = Array.prototype.slice.call(document.querySelectorAll('link[rel="stylesheet"][href]'));
        var matching = links.filter(function (link) { return link.href === href; });
        var persistent = matching.find(function (link) {
          return !bundle.sections.some(function (section) { return section.element.contains(link); });
        });
        if (!persistent) {
          var loaded = matching.find(function (link) { return Boolean(link.sheet); });
          if (!loaded) return; // A genuinely new stylesheet stays with its source.
          persistent = loaded.cloneNode(false);
          persistent.removeAttribute('id');
          persistent.setAttribute('data-sibsw-style', '');
          // Keep cascade order near the original section, outside the replaced DOM.
          bundle.primary.before(persistent);
        }
        incoming.remove();
        loads.push(new Promise(function (resolve, reject) {
          function finish(error) {
            persistent.removeEventListener('load', onLoad);
            persistent.removeEventListener('error', onError);
            signal.removeEventListener('abort', onAbort);
            if (error) reject(error); else resolve();
          }
          function onLoad() { finish(); }
          function onError() { finish(new Error('shared product stylesheet failed to load')); }
          function onAbort() { finish(abortError('stylesheet preparation aborted')); }
          persistent.addEventListener('load', onLoad);
          persistent.addEventListener('error', onError);
          signal.addEventListener('abort', onAbort, { once: true });
          if (signal.aborted) onAbort();
          else if (persistent.sheet) onLoad();
        }));
      });
    });
    return Promise.all(loads);
  }

  function replaceSectionReferences(root, from, to) {
    [root]
      .concat(Array.prototype.slice.call(root.querySelectorAll('*')))
      .forEach(function (node) {
        Array.prototype.slice.call(node.attributes || []).forEach(function (attribute) {
          if (attribute.value.indexOf(from) === -1) return;
          node.setAttribute(attribute.name, attribute.value.split(from).join(to));
        });
      });
  }

  function stagePrimarySection(primarySource, bundle, token) {
    var outgoing = bundle.primary;
    var rect = outgoing.getBoundingClientRect();
    var stagedId = bundle.primaryId + '--sibsw-stage-' + token;
    var originalStyle = primarySource.getAttribute('style');
    var originalAriaHidden = primarySource.getAttribute('aria-hidden');
    var originalInert = primarySource.hasAttribute('inert');

    replaceSectionReferences(primarySource, bundle.primaryId, stagedId);
    primarySource.setAttribute('data-sibsw-stage', String(token));
    primarySource.setAttribute('aria-hidden', 'true');
    primarySource.setAttribute('inert', '');
    primarySource.style.position = 'fixed';
    primarySource.style.top = rect.top + 'px';
    primarySource.style.left = rect.left + 'px';
    primarySource.style.width = rect.width + 'px';
    primarySource.style.maxWidth = rect.width + 'px';
    primarySource.style.visibility = 'hidden';
    // Every descendant is also forced visibility:hidden by sibling-swatches.css
    // (the theme's .product-form__cart-submit sets visibility:visible, which
    // punched through and painted the stage's red button on iOS). The dynamic
    // checkout button is the exception: it must really render while staged so
    // its 0.3s mount fade is over by commit. 1% group opacity + z-index:-1 keeps
    // it invisible (<= 2/255 colour difference) while it is still painted.
    primarySource.style.opacity = '0.01';
    primarySource.style.pointerEvents = 'none';
    primarySource.style.overflow = 'hidden';
    primarySource.style.zIndex = '-1';
    // product-content.js pins the live info column with an inline max-width
    // (538px) once, on first load. No stylesheet rule caps the column at or
    // below 1000px, so without the same pin the staged column laid out at the
    // full section width between ~540 and 1000px, failed the paint contract's
    // width check, and every switch fell back to a full navigation.
    var outgoingInfo = outgoing.querySelector('.product__info-wrapper');
    var stagedInfo = primarySource.querySelector('.product__info-wrapper');
    if (outgoingInfo && stagedInfo && outgoingInfo.style.maxWidth) {
      stagedInfo.style.maxWidth = outgoingInfo.style.maxWidth;
    }
    outgoing.before(primarySource);

    return {
      element: primarySource,
      stagedId: stagedId,
      originalStyle: originalStyle,
      originalAriaHidden: originalAriaHidden,
      originalInert: originalInert,
    };
  }

  function waitForImageDecode(image, signal, skipDecode) {
    return new Promise(function (resolve, reject) {
      var settled = false;

      function cleanup() {
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        if (signal) signal.removeEventListener('abort', onAbort);
      }

      function finish(error) {
        if (settled) return;
        settled = true;
        cleanup();
        if (error) {
          reject(error);
        } else if (!skipDecode && typeof image.decode === 'function') {
          image
            .decode()
            .catch(function () {})
            .then(resolve);
        } else {
          resolve();
        }
      }

      function onLoad() {
        if (!image.naturalWidth) finish(new Error('incoming image decoded blank'));
        else finish();
      }

      function onError() {
        finish(new Error('incoming image failed to load'));
      }

      function onAbort() {
        var error = new Error('staged switch aborted');
        error.name = 'AbortError';
        finish(error);
      }

      image.addEventListener('load', onLoad);
      image.addEventListener('error', onError);
      if (signal) signal.addEventListener('abort', onAbort, { once: true });

      if (signal && signal.aborted) onAbort();
      else if (image.complete) onLoad();
    });
  }

  function waitForPrimaryPaint(stage, layoutContract, signal) {
    var primary = stage.element;
    var swatchImages = Array.prototype.slice.call(primary.querySelectorAll('[data-sibling-swatches] img'));
    var hero =
      primary.querySelector('.product-single__gallery-item img[fetchpriority="high"]') ||
      primary.querySelector('.product-single__gallery-item img');

    // Swatch crops are the same bitmaps the live grid is painting. Calling
    // decode() on the staged copies made WebKit drop and re-decode the shared
    // bitmaps, so the LIVE grid flashed empty tiles mid-switch. Loaded is enough.
    return Promise.race([
      Promise.all(
        swatchImages
          .map(function (image) {
            return waitForImageDecode(image, signal, true);
          })
          .concat(hero ? [waitForImageDecode(hero, signal)] : [])
      ),
      new Promise(function (_, reject) {
        window.setTimeout(function () {
          reject(new Error('incoming images did not decode before the atomic-swap deadline'));
        }, 5000);
      }),
    ]).then(function () {
      return new Promise(function (resolve, reject) {
        var stableFrames = 0;
        var started = Date.now();
        var lastReadiness = null;

        function sample() {
          if (signal && signal.aborted) {
            var abortError = new Error('staged switch aborted');
            abortError.name = 'AbortError';
            reject(abortError);
            return;
          }

          var info = primary.querySelector('.product__info-wrapper');
          var slider = primary.querySelector('media-gallery .product-single__gallery');
          var activeHero =
            slider &&
            (slider.querySelector('.product-single__gallery-item.slick-current:not(.slick-cloned) img') ||
              slider.querySelector('.product-single__gallery-item.slick-current img'));
          var mediaWrapper = activeHero && activeHero.closest('.product-single__media-wrapper');
          var swatches = Array.prototype.slice.call(primary.querySelectorAll('[data-sibling-swatches] img'));
          var infoRect = info && info.getBoundingClientRect();
          var galleryRect = slider && slider.getBoundingClientRect();
          var mediaGalleryRect =
            slider && slider.closest('media-gallery') && slider.closest('media-gallery').getBoundingClientRect();
          var galleryHeight = galleryRect ? galleryRect.height : 0;
          var geometryReady =
            infoRect &&
            (!layoutContract.infoWidth || Math.abs(infoRect.width - layoutContract.infoWidth) <= 2) &&
            galleryHeight > 100;
          var galleryReady =
            slider &&
            slider.classList.contains('slick-initialized') &&
            slider.querySelector('.slick-list .slick-track') &&
            activeHero &&
            activeHero.complete &&
            activeHero.naturalWidth > 0;
          var swatchesReady =
            swatches.length > 0 &&
            swatches.every(function (image) {
              return image.complete && image.naturalWidth > 0;
            });
          var backingReady =
            mediaWrapper && getComputedStyle(mediaWrapper).backgroundColor === 'rgb(239, 238, 236)';
          lastReadiness = {
            infoWidth: infoRect && infoRect.width,
            expectedInfoWidth: layoutContract.infoWidth,
            galleryWidth: mediaGalleryRect && mediaGalleryRect.width,
            expectedGalleryWidth: layoutContract.galleryWidth,
            galleryHeight: galleryHeight,
            expectedGalleryHeight: layoutContract.galleryHeight,
            galleryReady: Boolean(galleryReady),
            swatchesReady: swatchesReady,
            backingReady: Boolean(backingReady),
          };

          if (geometryReady && galleryReady && swatchesReady && backingReady) stableFrames += 1;
          else stableFrames = 0;

          if (stableFrames >= 2) {
            resolve();
            return;
          }
          if (Date.now() - started >= 2500) {
            reject(
              new Error(
                'incoming gallery did not initialize to the stable paint contract: ' +
                  JSON.stringify(lastReadiness)
              )
            );
            return;
          }
          window.requestAnimationFrame(sample);
        }

        window.requestAnimationFrame(sample);
      });
    });
  }

  // Shop Pay / dynamic checkout: shopify-accelerated-checkout renders its
  // button with a 0.3s fade (`.wallet-button-fade-in { animation: .3s ... }` in
  // its closed shadow root; cannot be styled or finished from outside). The
  // server markup ships a light-DOM skeleton (`.shopify-payment-button__skeleton`)
  // that the component replaces when it renders, and the fade starts then.
  // Round 2 held the commit a fixed 380ms after staging; on a real iPhone the
  // button was still at 63-76% opacity in the commit frame. Either the render
  // (0-40ms after staging in emulation) lands later on the phone, or the fade
  // did not advance while the stage was not painted at all (opacity 0); the
  // stage is now painted at 1% (see stagePrimarySection) for the second case,
  // and for the first the gate follows the render itself: commit no earlier than
  // render + fade + margin, never earlier than the old 380ms floor, never later
  // than 700ms after staging. Only applied while the outgoing button is on
  // screen; the optimistic title / price / hero are already painted at tap, so
  // this only delays the swap of the rest of the section.
  var CHECKOUT_BUTTON_SETTLE = 380;
  var CHECKOUT_BUTTON_FADE = 300;
  var CHECKOUT_BUTTON_MARGIN = 100;
  var CHECKOUT_BUTTON_CAP = 700;

  function checkoutButtonRendered(host) {
    return !host.querySelector('.shopify-payment-button__skeleton');
  }

  function waitForCheckoutButton(staged, outgoing, stagedAt, signal) {
    if (!staged.querySelector('shopify-accelerated-checkout, .shopify-payment-button')) return Promise.resolve();
    var current = outgoing && outgoing.querySelector('.shopify-payment-button');
    var rect = current && current.getBoundingClientRect();
    if (!rect || !rect.height || rect.bottom <= 0 || rect.top >= window.innerHeight) return Promise.resolve();
    // Incoming colourway without a visible checkout button (e.g. sold out hides
    // it with display:none): nothing will fade in, nothing to wait for.
    var incoming = staged.querySelector('.shopify-payment-button');
    if (incoming && !incoming.getBoundingClientRect().height) return Promise.resolve();
    var host = staged.querySelector('shopify-accelerated-checkout');
    // No accelerated-checkout host (other button type): time-based as before.
    var renderedAt = !host || checkoutButtonRendered(host) ? stagedAt : null;
    return new Promise(function (resolve) {
      var timer = null;
      var observer = null;
      var done = false;

      function finish() {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        if (observer) observer.disconnect();
        observer = null;
        resolve();
      }

      function schedule() {
        var cap = stagedAt + CHECKOUT_BUTTON_CAP;
        var due =
          renderedAt === null
            ? cap
            : Math.min(
                cap,
                Math.max(stagedAt + CHECKOUT_BUTTON_SETTLE, renderedAt + CHECKOUT_BUTTON_FADE + CHECKOUT_BUTTON_MARGIN)
              );
        window.clearTimeout(timer);
        var wait = due - Date.now();
        if (wait <= 0) finish();
        else timer = window.setTimeout(finish, wait);
      }

      if (renderedAt === null && typeof MutationObserver === 'function') {
        observer = new MutationObserver(function () {
          if (renderedAt !== null || !checkoutButtonRendered(host)) return;
          renderedAt = Date.now();
          observer.disconnect();
          observer = null;
          schedule();
        });
        observer.observe(host, { childList: true, subtree: true });
      }
      if (signal) signal.addEventListener('abort', finish, { once: true });
      schedule();
    });
  }

  function cleanupStagedPrimary(stage) {
    if (stage && stage.element && stage.element.parentNode) stage.element.remove();
  }

  function restoreStagedPrimary(stage, bundle) {
    replaceSectionReferences(stage.element, stage.stagedId, bundle.primaryId);
    stage.element.removeAttribute('data-sibsw-stage');
    stage.element.classList.add('sibsw-fresh'); // released by releasePreApply after two frames
    if (stage.originalStyle === null) stage.element.removeAttribute('style');
    else stage.element.setAttribute('style', stage.originalStyle);
    if (stage.originalAriaHidden === null) stage.element.removeAttribute('aria-hidden');
    else stage.element.setAttribute('aria-hidden', stage.originalAriaHidden);
    if (!stage.originalInert) stage.element.removeAttribute('inert');
  }

  function swapSections(sources, bundle, stagedPrimary, scrollPosition) {
    var targetScroll = scrollPosition || { x: window.scrollX, y: window.scrollY };
    var locks = [];

    function restoreScroll() {
      if (window.scrollX !== targetScroll.x || window.scrollY !== targetScroll.y) {
        window.scrollTo(targetScroll.x, targetScroll.y);
      }
    }

    bundle.sections.forEach(function (item) {
      var source = sources[item.id];
      if (!source) return;
      var element = item.element;
      locks.push({
        element: element,
        minHeight: element.style.minHeight,
        overflowAnchor: element.style.overflowAnchor,
        lockedHeight: element.getBoundingClientRect().height + 'px',
      });
      element.style.minHeight = locks[locks.length - 1].lockedHeight;
      element.style.overflowAnchor = 'none';
    });

    // The stage was inserted directly before the outgoing section, so removing the
    // outgoing node commits it WITHOUT moving it. A move (replaceWith) disconnects
    // and reconnects every custom element inside, which re-mounted Shopify's
    // accelerated checkout (Shop Pay button blank, then fading in) and restarted
    // CSS entrance animations right after every switch.
    committing = true;
    try {
      if (stagedPrimary.element.nextElementSibling === bundle.primary) bundle.primary.remove();
      else bundle.primary.replaceWith(stagedPrimary.element);
      restoreStagedPrimary(stagedPrimary, bundle);
    } finally {
      committing = false;
    }

    bundle.sections.forEach(function (item) {
      if (item.id === bundle.primaryId) return;
      var source = sources[item.id];
      if (source) item.element.innerHTML = source.innerHTML;
    });

    var freshPrimary = stagedPrimary.element;
    var freshPrimaryLock = locks.find(function (lock) {
      return lock.element === bundle.primary;
    });
    if (freshPrimaryLock) {
      freshPrimaryLock.element = freshPrimary;
      freshPrimary.style.minHeight = freshPrimaryLock.lockedHeight;
      freshPrimary.style.overflowAnchor = 'none';
    }

    restoreScroll();
    window.requestAnimationFrame(function () {
      restoreScroll();
      window.requestAnimationFrame(function () {
        locks.forEach(function (lock) {
          lock.element.style.minHeight = lock.minHeight;
          lock.element.style.overflowAnchor = lock.overflowAnchor;
        });
        restoreScroll();
        // Releasing the temporary height locks can itself trigger browser
        // scroll anchoring after this callback. Two final frames catch that
        // post-layout adjustment without holding the user's scroll longer.
        window.requestAnimationFrame(function () {
          restoreScroll();
          window.requestAnimationFrame(restoreScroll);
        });
      });
    });
    return freshPrimary;
  }

  function stabilizeScroll(scrollPosition, token, swatchAnchor) {
    var cancelled = false;
    var started = Date.now();
    var events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];

    function cleanup() {
      events.forEach(function (eventName) {
        window.removeEventListener(eventName, cancel);
      });
    }

    function cancel() {
      cancelled = true;
      cleanup();
    }

    events.forEach(function (eventName) {
      window.addEventListener(eventName, cancel, { passive: true });
    });

    function tick() {
      if (cancelled || token !== switchToken) {
        cleanup();
        return;
      }
      if (swatchAnchor && swatchAnchor.handle) {
        var liveRoot = getRoot();
        var liveTile =
          liveRoot &&
          Array.prototype.find.call(
            liveRoot.querySelectorAll('[data-sibling-swatches] a[data-sibling-handle]'),
            function (tile) {
              return tile.getAttribute('data-sibling-handle') === swatchAnchor.handle;
            }
          );
        var liveTop = liveTile && liveTile.getBoundingClientRect().top;
        if (Number.isFinite(liveTop) && Math.abs(liveTop - swatchAnchor.top) > 0.5) {
          scrollPosition.y = Math.max(0, scrollPosition.y + liveTop - swatchAnchor.top);
        }
      }
      if (window.scrollX !== scrollPosition.x || window.scrollY !== scrollPosition.y) {
        window.scrollTo(scrollPosition.x, scrollPosition.y);
      }
      if (Date.now() - started < 450) window.requestAnimationFrame(tick);
      else cleanup();
    }

    window.requestAnimationFrame(tick);
  }

  function markTileLoading(tile, token) {
    if (!tile) return;
    tile.classList.add('is-loading');
    tile.setAttribute('aria-busy', 'true');
    tile.dataset.sibswLoadingToken = String(token);
  }

  function clearTileLoading(tile, token) {
    if (!tile) return;
    if (token && tile.dataset.sibswLoadingToken !== String(token)) return;
    tile.classList.remove('is-loading');
    tile.removeAttribute('aria-busy');
    delete tile.dataset.sibswLoadingToken;
  }

  function restoreActiveSwatchFocus(section, shouldRestore) {
    if (!section || !shouldRestore) return;
    var active = section.querySelector('[data-sibling-swatches] a[aria-current="true"]');
    if (!active) return;
    try {
      active.focus({ preventScroll: true });
    } catch (error) {
      active.focus();
    }
  }

  function clearTransitionArtifacts() {
    // Never touch the stage while swapSections is mid-commit: DOM removal can
    // dispatch load/pageshow synchronously and re-enter here.
    if (committing) return;
    document.querySelectorAll('[data-sibsw-stage]').forEach(function (stage) {
      stage.remove();
    });
    document.querySelectorAll('.sibsw-switching').forEach(function (section) {
      section.classList.remove('sibsw-switching');
    });
    document.querySelectorAll('[data-sibsw-loading-token]').forEach(function (tile) {
      clearTileLoading(tile);
    });
  }

  function abortPendingWork() {
    switchToken += 1;
    pending = null;
    revertOptimistic();
    window.clearTimeout(hoverTimer);
    if (switchController) switchController.abort();
    if (prefetchController) prefetchController.abort();
    switchController = null;
    prefetchController = null;
    prefetchHref = null;
    inFlight.forEach(function (entry) {
      entry.controller.abort();
    });
    inFlight.clear();
    clearTransitionArtifacts();
    if (liveRegion) liveRegion.textContent = '';
  }

  function markHistoryEntry() {
    try {
      var st = history.state || {};
      if (!st.soollaSwitch && location.pathname.indexOf('/products/') !== -1 && getRoot()) {
        st.soollaSwitch = true;
        history.replaceState(st, '', location.href);
      }
    } catch (e) {
      /* history API unavailable — degrade to full navigations */
    }
  }

  /* ---------------------------------------------------------------------------
   * Optimistic buy box (switch latency, 2026-09-25)
   * ------------------------------------------------------------------------ */

  function swatchConfig(root) {
    var picker = root && root.querySelector('[data-sibling-swatches]');
    return {
      picker: picker,
      optimistic: Boolean(picker && picker.hasAttribute('data-sibsw-optimistic')),
    };
  }

  function tileData(tile) {
    if (!tile) return null;
    try {
      return JSON.parse(tile.getAttribute('data-sibsw-opt') || 'null');
    } catch (e) {
      return null;
    }
  }

  function findTileForPath(root, path) {
    if (!root || !path) return null;
    return Array.prototype.find.call(
      root.querySelectorAll('[data-sibling-swatches] a[data-sibling-product]'),
      function (tile) {
        try {
          return new URL(tile.getAttribute('href'), window.location.origin).pathname === path;
        } catch (e) {
          return false;
        }
      }
    );
  }

  function liveHeroImg(root) {
    return (
      root.querySelector(
        'media-gallery .product-single__gallery-item.slick-current:not(.slick-cloned) img.product-featured-media'
      ) || root.querySelector('media-gallery .product-single__gallery-item img.product-featured-media')
    );
  }

  function heroSources(data, liveHero) {
    if (!data || !data.i) return null;
    var base = data.i;
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    // Same candidates, src and sizes as snippets/custom-media.liquid, so the
    // committed gallery image resolves to the already-decoded cache entry.
    var srcset = [400, 600, 800, 1200, 1600]
      .filter(function (w) {
        return data.w >= w;
      })
      .map(function (w) {
        return base + sep + 'width=' + w + ' ' + w + 'w';
      })
      .join(', ');
    return {
      src: base + sep + 'width=800',
      srcset: srcset,
      sizes: (liveHero && liveHero.getAttribute('sizes')) || '(min-width: 1000px) 600px, 100vw',
    };
  }

  function preloadHero(sources) {
    var key = sources.srcset + '|' + sources.src + '|' + sources.sizes;
    var hit = heroCache.get(key);
    if (hit) return hit;
    var img = new Image();
    img.alt = '';
    img.decoding = 'async';
    img.setAttribute('fetchpriority', 'high');
    img.sizes = sources.sizes;
    if (sources.srcset) img.srcset = sources.srcset;
    img.src = sources.src;
    var promise = (typeof img.decode === 'function'
      ? img.decode()
      : new Promise(function (resolve, reject) {
          img.onload = resolve;
          img.onerror = reject;
        })
    ).then(function () {
      if (!img.naturalWidth) throw new Error('hero decoded blank');
      return img;
    });
    var entry = { img: img, promise: promise };
    heroCache.set(key, entry);
    while (heroCache.size > 8) heroCache.delete(heroCache.keys().next().value);
    promise.catch(function () {
      if (heroCache.get(key) === entry) heroCache.delete(key);
    });
    return entry;
  }

  // The warm paper surface lives on the gallery's media wrapper today; start at
  // the hero itself so a backing moved onto the <img> is still honoured.
  function backdropColor(node) {
    var el = node;
    while (el && el !== document.documentElement) {
      var bg = getComputedStyle(el).backgroundColor;
      if (bg && bg !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(bg)) return bg;
      el = el.parentElement;
    }
    return 'rgb(239, 238, 236)';
  }

  function setImportant(node, prop, value) {
    node.style.setProperty(prop, value, 'important');
  }

  function showHeroOverlay(state, img) {
    if (optimistic !== state || !state.section.isConnected) return;
    var hero = liveHeroImg(state.root);
    var parent = hero && hero.parentElement;
    if (!parent || !hero.offsetWidth || !hero.offsetHeight) return;
    if (getComputedStyle(parent).position === 'static') {
      state.parentPosition = { node: parent, value: parent.style.position };
      parent.style.position = 'relative';
    }
    // media__transparent: the overlay is appended inside `.media`, and base.css
    // paints every `.media > *:not(.media__transparent):not(.loaded)...` child
    // with the grey animated skeleton gradient. That gradient sat on top of the
    // paper backdrop for the whole optimistic window (desktop: a cool grey box
    // until commit). The class opts the overlay out, exactly like the real hero.
    img.className = 'sibsw-hero-overlay media__transparent';
    img.setAttribute('data-sibsw-hero-overlay', '');
    img.setAttribute('aria-hidden', 'true');
    img.removeAttribute('style');
    setImportant(img, 'left', hero.offsetLeft + 'px');
    setImportant(img, 'top', hero.offsetTop + 'px');
    setImportant(img, 'width', hero.offsetWidth + 'px');
    setImportant(img, 'height', hero.offsetHeight + 'px');
    setImportant(img, 'max-width', 'none');
    setImportant(img, 'margin', '0');
    setImportant(img, 'display', 'block');
    img.style.backgroundColor = backdropColor(hero);
    parent.appendChild(img);
    state.overlay = img;
    window.requestAnimationFrame(function () {
      if (state.overlay === img) img.classList.add('is-visible');
    });
  }

  function replaceTrimmedText(node, value) {
    if (!node) return;
    var text = node.textContent;
    var trimmed = text.trim();
    node.textContent = trimmed ? text.replace(trimmed, value) : value;
  }

  function revertOptimistic() {
    var state = optimistic;
    if (!state) return;
    optimistic = null;
    state.restores.forEach(function (restore) {
      try {
        restore();
      } catch (e) {
        /* the node may already be gone */
      }
    });
    if (state.overlay && state.overlay.parentNode) state.overlay.remove();
    if (state.parentPosition) state.parentPosition.node.style.position = state.parentPosition.value;
  }

  function applyOptimistic(root, tile, token) {
    revertOptimistic();
    var config = swatchConfig(root);
    var section = getSectionEl(root);
    if (!section || !tile || !config.picker) return null;
    var state = {
      token: token,
      root: root,
      section: section,
      restores: [],
      overlay: null,
      optimistic: false,
      deferredAdd: null,
    };
    optimistic = state;

    function keepAttr(node, name) {
      if (!node) return;
      var had = node.hasAttribute(name);
      var value = node.getAttribute(name);
      state.restores.push(function () {
        if (had) node.setAttribute(name, value);
        else node.removeAttribute(name);
      });
    }

    // Instant acknowledgement: the selected ring moves to the tapped tile.
    // aria-current stays on the committed tile until the swap commits.
    Array.prototype.forEach.call(config.picker.querySelectorAll('a.is-current'), function (current) {
      if (current === tile) return;
      keepAttr(current, 'class');
      current.classList.remove('is-current');
    });
    keepAttr(tile, 'class');
    tile.classList.add('is-current');

    var info = root.querySelector('.product__info-container');
    if (info) {
      keepAttr(info, 'aria-busy');
      info.setAttribute('aria-busy', 'true');
    }

    var data = config.optimistic ? tileData(tile) : null;
    if (!data || !data.t) return state;
    state.optimistic = true;

    // H1: two-tier when the target rebuilds exactly (same rule as the section).
    var h1 = root.querySelector('h1.product__title');
    if (h1) {
      var family = h1.querySelector('.product__title-family');
      var edition = h1.querySelector('.product__title-edition');
      var tieredTarget = data.f && data.e && data.f + ', ' + data.e === data.t;
      keepAttr(h1, 'aria-label');
      keepAttr(h1, 'class');
      var html = h1.innerHTML;
      state.restores.push(function () {
        h1.innerHTML = html;
      });
      if (family && edition && tieredTarget) {
        var familyText = family.firstChild;
        if (familyText && familyText.nodeType === 3 && familyText.nodeValue !== data.f) familyText.nodeValue = data.f;
        edition.textContent = data.e;
        h1.setAttribute('aria-label', data.t);
      } else if (!family && !edition) {
        replaceTrimmedText(h1, data.t);
      } else {
        h1.classList.remove('product__title--tiered');
        h1.removeAttribute('aria-label');
        h1.textContent = data.t;
      }
    }

    // Price: text + sale / sold-out state of the existing custom-price markup.
    var price = root.querySelector('.product__price .price');
    if (price && data.p && data.z > 0 && !price.classList.contains('price--free')) {
      var regular = price.querySelector('.price__regular .price-item--regular');
      var sale = price.querySelector('.price__sale .price-item--sale');
      var compare = price.querySelector('.price__compare .price-item--regular');
      [regular, sale, compare].forEach(function (node) {
        if (!node) return;
        var text = node.textContent;
        state.restores.push(function () {
          node.textContent = text;
        });
      });
      keepAttr(price, 'class');
      replaceTrimmedText(regular, data.p);
      replaceTrimmedText(sale, data.p);
      if (data.c) replaceTrimmedText(compare, data.c);
      price.classList.toggle('price--on-sale', Boolean(data.c));
      price.classList.toggle('price--sold-out', data.a === false);
    }

    // Hero: decode first, then cross-fade over the outgoing hero.
    var sources = heroSources(data, liveHeroImg(root));
    if (sources) {
      preloadHero(sources).promise.then(
        function (img) {
          showHeroOverlay(state, img);
        },
        function () {
          /* the committed gallery still brings the hero */
        }
      );
    }
    return state;
  }

  function isPurchaseControl(target) {
    return (
      target &&
      typeof target.closest === 'function' &&
      target.closest(
        'form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], .shopify-payment-button, shopify-accelerated-checkout, shopify-buy-it-now-button'
      )
    );
  }

  // While the optimistic buy box is ahead of the (still outgoing) product form,
  // hold purchase taps so the cart can never get the colourway the shopper has
  // just switched away from. A plain add is replayed once the swap commits.
  function guardPurchase(e) {
    var staged = isPurchaseControl(e.target);
    if (staged && staged.closest('[data-sibsw-stage]')) {
      // The hidden, inert stage is never a purchase surface.
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    var state = optimistic;
    if (!state || !state.optimistic) return;
    var control = isPurchaseControl(e.target);
    if (!control || !state.section.contains(control)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    // Only the theme's own add-to-cart buttons are replayed (not dynamic checkout).
    // NB: read the id ATTRIBUTE — form.id is shadowed by the form's input[name="id"].
    if (e.type === 'click' && control.closest('product-form') && control.matches('button[type="submit"], input[type="submit"]')) {
      state.deferredAdd = {
        formId: control.form ? control.form.getAttribute('id') : null,
        fixed: control.classList.contains('product-form__cart-submit--fix'),
      };
    }
  }

  function guardSubmit(e) {
    var state = optimistic;
    var form = e.target;
    if (form && typeof form.closest === 'function' && form.closest('[data-sibsw-stage]')) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    if (!state || !state.optimistic || !form || typeof form.getAttribute !== 'function') return;
    if (!/\/cart\/add/.test(form.getAttribute('action') || '') || !state.section.contains(form)) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (form.closest('product-form')) state.deferredAdd = { formId: form.getAttribute('id'), fixed: false };
  }

  function replayDeferredAdd(section, deferred) {
    if (!section || !deferred) return;
    window.requestAnimationFrame(function () {
      var buttons = Array.prototype.filter.call(
        section.querySelectorAll('product-form button[type="submit"]'),
        function (button) {
          return (
            !button.disabled &&
            (!deferred.formId || (button.form && button.form.getAttribute('id') === deferred.formId))
          );
        }
      );
      var button =
        buttons.find(function (candidate) {
          return candidate.classList.contains('product-form__cart-submit--fix') === deferred.fixed;
        }) || buttons[0];
      if (button) button.click();
    });
  }

  function idlePrefetchAllowed() {
    var connection = navigator.connection;
    if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return false;
    return idleBudget > 0;
  }

  function scheduleNeighbourPrefetch(extraHrefs) {
    if (!idlePrefetchAllowed()) return;
    var run = function () {
      var root = getRoot();
      var bundle = root && getSectionBundle(root);
      if (!bundle) return;
      var tiles = Array.prototype.slice.call(root.querySelectorAll('[data-sibling-swatches] a[data-sibling-product]'));
      var index = tiles.findIndex(function (tile) {
        return tile.getAttribute('aria-current') === 'true';
      });
      var hrefs = (extraHrefs || []).slice();
      [index + 1, index - 1].forEach(function (i) {
        if (index !== -1 && tiles[i]) hrefs.push(tiles[i].getAttribute('href'));
      });
      hrefs.forEach(function (href) {
        if (!href || !idlePrefetchAllowed() || idleInFlight >= 3) return;
        var path = new URL(href, window.location.origin).pathname;
        if (path === window.location.pathname) return;
        var key = requestKeyFor(href, bundle);
        if (cacheGet(key) || inFlight.has(key)) return;
        idleBudget -= 1;
        idleInFlight += 1;
        var controller = new AbortController();
        fetchSections(href, bundle, controller.signal)
          .catch(function () {
            /* best effort */
          })
          .finally(function () {
            idleInFlight -= 1;
          });
      });
    };
    window.setTimeout(function () {
      if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 });
      else run();
    }, 600);
  }

  /* Sticky mobile add-to-cart bar (product-form.js toggles
   * .fix-controls-group--submit on scroll). The incoming bar inherits the
   * outgoing bar's state, and scroll-driven toggles are frozen from the commit
   * until the post-swap scroll stabilisation has finished; then the live form
   * re-evaluates once. The staged copy can never show (sibling-swatches.css). */
  var stickyFreezeTimer = null;

  function carryStickyState(fromSection, toSection) {
    var from = fromSection && fromSection.querySelector('[data-controls-group-submit]');
    var to = toSection && toSection.querySelector('[data-controls-group-submit]');
    if (!from || !to) return;
    to.classList.toggle('fix-controls-group--submit', from.classList.contains('fix-controls-group--submit'));
  }

  function freezeSticky(token) {
    document.documentElement.setAttribute('data-sibsw-sticky-freeze', '');
    window.clearTimeout(stickyFreezeTimer);
    stickyFreezeTimer = window.setTimeout(function () {
      document.documentElement.removeAttribute('data-sibsw-sticky-freeze');
      var root = getRoot();
      var form = root && root.querySelector('product-form');
      if (form && typeof form.onScroll === 'function') {
        try {
          form.onScroll();
        } catch (e) {
          /* the next real scroll re-evaluates anyway */
        }
      }
    }, 500);
  }

  function switchTo(href, opts) {
    opts = opts || {};
    var root = getRoot();
    var bundle = root && getSectionBundle(root);
    var sectionEl = bundle && bundle.primary;
    if (!root || !bundle || !sectionEl) {
      window.location.assign(href);
      return;
    }
    var fromCtx = getContext(sectionEl);
    var preservedState = captureTransientState(root);
    var layoutContract = captureLayoutContract(sectionEl);
    var familyReviewState = captureFamilyReviewState(sectionEl);
    var scrollPosition = { x: window.scrollX, y: window.scrollY };
    var swatchAnchor = captureSwatchAnchor(opts.tile);
    var committedScrollPosition = scrollPosition;
    var shouldRestoreFocus = Boolean(opts.tile && document.activeElement === opts.tile);
    var token = ++switchToken;
    var stagedPrimary = null;
    var switchTimedOut = false;

    if (switchController) switchController.abort();
    var controller = new AbortController();
    switchController = controller;
    if (
      prefetchController &&
      prefetchHref &&
      requestKeyFor(prefetchHref, bundle) !== requestKeyFor(href, bundle)
    ) {
      prefetchController.abort();
      prefetchController = null;
      prefetchHref = null;
    }
    var switchDeadline = window.setTimeout(function () {
      switchTimedOut = true;
      controller.abort();
    }, SWITCH_TIMEOUT);

    var targetPath = new URL(href, window.location.origin).pathname;
    pending = { path: targetPath, token: token };
    var tile = opts.tile || findTileForPath(root, targetPath);
    var optimisticState = applyOptimistic(root, tile, token);

    sectionEl.classList.add('sibsw-switching');
    markTileLoading(opts.tile, token);
    if (opts.label) announce('Loading ' + opts.label + '…');

    fetchSections(href, bundle, controller.signal)
      .then(function (sections) {
        if (token !== switchToken) return null;
        var sources = prepareSectionSources(sections, bundle);
        return preserveSharedStyles(sources, bundle, controller.signal).then(function () {
          return sources;
        });
      })
      .then(function (sources) {
        if (!sources || token !== switchToken) return null;
        // Stage as soon as the HTML is ready, so Shopify's dynamic checkout
        // button mounts (and runs its own fade-in) while still hidden, in
        // parallel with the image preloads.
        stagedPrimary = stagePrimarySection(sources[bundle.primaryId], bundle, token);
        preApplyBundleSelection(stagedPrimary.element, preservedState);
        var stagedAt = Date.now();
        return Promise.all([
          preloadSectionImages(stagedPrimary.element),
          waitForCheckoutButton(stagedPrimary.element, sectionEl, stagedAt, controller.signal),
        ])
          .then(function () {
            if (token !== switchToken) return null;
            return waitForPrimaryPaint(stagedPrimary, layoutContract, controller.signal);
          })
          .then(function () {
            return sources;
          });
      })
      .then(function (sources) {
        if (!sources) return;
        if (token !== switchToken) {
          cleanupStagedPrimary(stagedPrimary);
          stagedPrimary = null;
          return;
        }
        // The shopper can scroll or change the bundle while images load.
        // Preserve the outgoing UI at commit time, not its stale click-time state.
        preservedState = captureTransientState(root);
        preApplyBundleSelection(stagedPrimary.element, preservedState);
        var currentInfo = sectionEl.querySelector('.product__info-wrapper');
        if (currentInfo) {
          var currentInfoRect = currentInfo.getBoundingClientRect();
          layoutContract.infoTop = currentInfoRect.top;
          layoutContract.infoX = currentInfoRect.x;
        }
        committedScrollPosition.x = window.scrollX;
        committedScrollPosition.y = window.scrollY;
        swatchAnchor = captureSwatchAnchor(opts.tile);
        shouldRestoreFocus = Boolean(opts.tile && document.activeElement === opts.tile);
        var deferredAdd = optimisticState && optimistic === optimisticState ? optimisticState.deferredAdd : null;
        carryStickyState(sectionEl, stagedPrimary.element);
        freezeSticky(token);
        var freshSection = swapSections(sources, bundle, stagedPrimary, committedScrollPosition);
        stagedPrimary = null;
        // The optimistic layer lived on the outgoing nodes, which are gone now.
        if (optimistic === optimisticState) optimistic = null;
        // Apply the published PDP width contract in the same task as the DOM
        // swap so no intermediate frame can paint the unpinned info column.
        restoreLayoutContract(freshSection, layoutContract);
        // Sibling colourways share a Loox review group. Preserve the live top
        // rating node, while the below-fold review section remains outside the
        // fetched/swapped bundle entirely, so neither widget reloads.
        restoreFamilyReviewState(freshSection, familyReviewState);
        // The incoming title/rating/bundle content may be taller or shorter.
        // Keep the tile the shopper actually touched at the same viewport
        // position, and update the shared scroll target before any rAF lock
        // callback can paint the old coordinate.
        restoreSwatchAnchor(freshSection, swatchAnchor, committedScrollPosition);
        // Clear the transition marker on the newly committed section.
        freshSection.classList.remove('sibsw-switching');
        var ctx = getContext(freshSection);

        if (opts.push !== false) {
          history.pushState({ soollaSwitch: true }, '', ctx ? ctx.url : new URL(href, location.origin).pathname);
        }
        updateHead(ctx);
        track(ctx, fromCtx);
        restoreActiveSwatchFocus(freshSection, shouldRestoreFocus);
        if (ctx) announce(ctx.title + ' loaded');

        document.dispatchEvent(
          new CustomEvent('soolla:product-swapped', {
            detail: { context: ctx, section: freshSection, preservedState: preservedState },
          })
        );
        // Gallery/custom-element hydration can adjust the document height a
        // few hundred milliseconds after the HTML swap. Hold the original
        // viewport through that settling window, but immediately stop if the
        // shopper starts a wheel, touch, pointer, or keyboard interaction.
        stabilizeScroll(committedScrollPosition, token, swatchAnchor);
        releasePreApply(freshSection);
        replayDeferredAdd(freshSection, deferredAdd);
        scheduleNeighbourPrefetch(fromCtx && fromCtx.url ? [fromCtx.url] : []);
        // NOTE: intentionally NOT dispatching a synthetic window resize. The
        // theme's collection-margin script responds to resize with a
        // 300ms-delayed remeasure, which reads as content "jumping" ~⅓s after
        // the swap. In-section scripts re-init from 'soolla:product-swapped'
        // above; the theme's own ResizeObserver catches real height changes.
      })
      .catch(function (err) {
        cleanupStagedPrimary(stagedPrimary);
        stagedPrimary = null;
        clearTileLoading(opts.tile, token);
        if (switchTimedOut) {
          err = new Error('sibling switch exceeded ' + SWITCH_TIMEOUT + 'ms');
          err.name = 'TimeoutError';
        }
        if (err && err.name === 'AbortError') return; // superseded — newer click owns the UI
        if (token !== switchToken) return;
        sectionEl.classList.remove('sibsw-switching');
        try {
          console.error('[Soolla sibling switcher] Falling back to navigation:', err);
        } catch (logError) {
          /* console unavailable */
        }
        // Any real failure: restore normal navigation. Worst case = today.
        window.location.assign(href);
      })
      .finally(function () {
        window.clearTimeout(switchDeadline);
        if (switchController === controller) switchController = null;
        if (pending && pending.token === token) pending = null;
      });
  }

  function prefetch(href) {
    var root = getRoot();
    if (!root) return;
    if (navigator.connection && navigator.connection.saveData) return;
    var bundle = getSectionBundle(root);
    if (!bundle) return;
    var key = requestKeyFor(href, bundle);
    if (cacheGet(key)) return;
    if (
      prefetchController &&
      prefetchHref &&
      requestKeyFor(prefetchHref, bundle) === key
    ) {
      return;
    }
    if (prefetchController) prefetchController.abort();
    var controller = new AbortController();
    prefetchController = controller;
    prefetchHref = href;
    fetchSections(href, bundle, controller.signal)
      .catch(function () {
        /* prefetch is best-effort */
      })
      .finally(function () {
        if (prefetchController === controller) {
          prefetchController = null;
          prefetchHref = null;
        }
      });
  }

  function findSwatchAnchor(target) {
    if (!target || typeof target.closest !== 'function') return null;
    if (target.closest('[data-sibsw-stage]')) return null; // never act on the hidden stage
    return target.closest('[data-sibling-swatches] a[data-sibling-product]');
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = findSwatchAnchor(e.target);
    if (!a) return;
    if (a.getAttribute('aria-current') === 'true') {
      e.preventDefault();
      abortPendingWork(); // Reselecting the displayed colour is the latest intent.
      return; // already on this colorway
    }
    e.preventDefault();
    var path = new URL(a.getAttribute('href'), window.location.origin).pathname;
    if (pending && pending.path === path) return; // same target already in flight: no double fetch
    switchTo(a.getAttribute('href'), {
      tile: a,
      label: a.getAttribute('data-sibling-label') || '',
    });
  });

  ['mouseover', 'focusin', 'touchstart'].forEach(function (evt) {
    document.addEventListener(
      evt,
      function (e) {
        var a = findSwatchAnchor(e.target);
        if (!a || a.getAttribute('aria-current') === 'true') return;
        clearTimeout(hoverTimer);
        var href = a.getAttribute('href');
        if (evt === 'mouseover') {
          hoverTimer = setTimeout(function () {
            prefetch(href);
          }, 65);
        } else {
          prefetch(href);
        }
      },
      evt === 'touchstart' ? { passive: true } : false
    );
  });

  document.addEventListener(
    'pointerdown',
    function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return; // touch prefetches on touchstart
      var a = findSwatchAnchor(e.target);
      if (!a || a.getAttribute('aria-current') === 'true') return;
      clearTimeout(hoverTimer);
      prefetch(a.getAttribute('href'));
    },
    { passive: true }
  );

  // Window capture runs before the bundle widget's document-capture submit
  // handler (registered earlier by an inline script), so a held purchase can
  // never be intercepted by it first.
  window.addEventListener('click', guardPurchase, true);
  window.addEventListener('submit', guardSubmit, true);

  document.addEventListener('mouseout', function (e) {
    if (findSwatchAnchor(e.target) && !findSwatchAnchor(e.relatedTarget || null)) {
      clearTimeout(hoverTimer);
    }
  });

  window.addEventListener('popstate', function (e) {
    // Only handle traversals between product pages we created ourselves.
    if (!e.state || !e.state.soollaSwitch) return;
    if (location.pathname.indexOf('/products/') === -1) return;
    switchTo(location.pathname, { push: false });
  });

  window.addEventListener('pagehide', abortPendingWork);
  window.addEventListener('pageshow', function (event) {
    // A restored page must never retain a half-finished hidden stage or stale
    // commerce HTML from before it entered the back-forward cache. Only a
    // back-forward restore may clear artifacts: the ordinary pageshow that
    // follows `load` can fire synchronously INSIDE a commit (removing the
    // outgoing section completes the document load when it held the last
    // pending image), and clearing there deleted the incoming buy box
    // (Codex QA run-03, restored-page case).
    if (event.persisted) {
      clearTransitionArtifacts();
      cache.clear();
    }
    pinStaticAssets();
    markHistoryEntry();
  });
  window.addEventListener('online', function () {
    // Prices and availability may have changed while the device was offline.
    cache.clear();
  });

  // Mark the entry state so traversing BACK to it is also handled in place.
  pinStaticAssets();
  markHistoryEntry();
})();
